import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, AlertTriangle } from 'lucide-react';

const faqs = [
  {
    q: '¿Es obligatorio indicar los alérgenos en la carta del restaurante?',
    a: 'Sí. Según el Reglamento (UE) 1169/2011 y el Real Decreto 126/2015, todos los establecimientos de hostelería en España están obligados a informar a los clientes sobre la presencia de los 14 alérgenos de declaración obligatoria en sus platos.',
  },
  {
    q: '¿Cuáles son los 14 alérgenos obligatorios?',
    a: 'Los 14 alérgenos de declaración obligatoria son: gluten, crustáceos, huevos, pescado, cacahuetes, soja, leche y lácteos, frutos de cáscara (nueces, almendras, avellanas, etc.), apio, mostaza, sésamo, dióxido de azufre y sulfitos, altramuces y moluscos.',
  },
  {
    q: '¿Qué multas hay por no declarar los alérgenos?',
    a: 'Las sanciones varían: leves de 100€ a 5.000€ por errores menores, graves de 5.001€ a 20.000€ cuando hay negligencia, y muy graves de 20.001€ hasta 600.000€ cuando el incumplimiento supone un riesgo para la salud o existe ocultación deliberada.',
  },
  {
    q: '¿Cómo gestiona PLEKO los alérgenos?',
    a: 'En PLEKO puedes asignar alérgenos a cada ingrediente. Cuando creas una receta, el sistema muestra automáticamente todos los alérgenos presentes en el plato según sus ingredientes. Puedes mostrar esta información en tu carta digital.',
  },
  {
    q: '¿Necesito una carta de alérgenos separada?',
    a: 'No necesariamente una carta separada, pero sí debes informar de alguna forma visible: iconos en la carta, carta de alérgenos aparte, o información disponible bajo petición con cartel visible. Con la carta digital de PLEKO, los alérgenos se muestran automáticamente junto a cada plato.',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const alergenos = [
  { nombre: 'Gluten', desc: 'Trigo, centeno, cebada, avena, espelta', icono: '🌾' },
  { nombre: 'Crustáceos', desc: 'Gambas, langostinos, cangrejos, bogavante', icono: '🦐' },
  { nombre: 'Huevos', desc: 'Huevos y derivados (mayonesa, rebozados, pastas)', icono: '🥚' },
  { nombre: 'Pescado', desc: 'Todas las especies de pescado y derivados', icono: '🐟' },
  { nombre: 'Cacahuetes', desc: 'Cacahuetes y productos a base de cacahuetes', icono: '🥜' },
  { nombre: 'Soja', desc: 'Soja y productos derivados (salsa de soja, tofu)', icono: '🫘' },
  { nombre: 'Leche', desc: 'Leche y derivados (queso, mantequilla, nata)', icono: '🥛' },
  { nombre: 'Frutos de cáscara', desc: 'Almendras, avellanas, nueces, pistachos, anacardos', icono: '🌰' },
  { nombre: 'Apio', desc: 'Apio y productos derivados', icono: '🥬' },
  { nombre: 'Mostaza', desc: 'Mostaza y productos derivados', icono: '🟡' },
  { nombre: 'Sésamo', desc: 'Semillas de sésamo y derivados', icono: '⚪' },
  { nombre: 'Sulfitos', desc: 'Dióxido de azufre y sulfitos (>10mg/kg)', icono: '🍷' },
  { nombre: 'Altramuces', desc: 'Altramuces y productos derivados', icono: '🟤' },
  { nombre: 'Moluscos', desc: 'Mejillones, almejas, pulpo, calamar', icono: '🐙' },
];

export default function AlergenosPage() {
  return (
    <>
      <Helmet>
        <title>Alérgenos en Restaurantes: Normativa 2026 y Cómo Cumplirla | PLEKO</title>
        <meta
          name="description"
          content="Guía completa de alérgenos obligatorios en restaurantes España 2026. Los 14 alérgenos, normativa vigente, multas por incumplimiento y cómo gestionarlos automáticamente."
        />
        <meta name="keywords" content="alérgenos restaurante, carta alérgenos obligatoria, normativa alérgenos hostelería 2026, 14 alérgenos obligatorios, multas alérgenos restaurante, gestión alérgenos" />
        <link rel="canonical" href="https://pleko.app/alergenos-restaurante" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/alergenos-restaurante" />
        <meta property="og:title" content="Alérgenos en Restaurantes: Normativa 2026 y Cómo Cumplirla | PLEKO" />
        <meta property="og:description" content="Guía completa de alérgenos obligatorios en restaurantes España 2026. Los 14 alérgenos, normativa vigente, multas por incumplimiento y cómo gestionarlos automáticamente." />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Alérgenos en Restaurantes: Normativa 2026 y Cómo Cumplirla | PLEKO" />
        <meta name="twitter:description" content="Guía completa de alérgenos obligatorios en restaurantes España 2026. Los 14 alérgenos, normativa vigente, multas por incumplimiento y cómo gestionarlos automáticamente." />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-red-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-6">
              <ShieldCheck className="w-4 h-4" />
              Normativa 2026
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Alérgenos en restaurantes:<br />
              <span className="text-red-500">normativa y cómo cumplirla</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Multas de hasta 600.000€ por no declarar alérgenos. Conoce la normativa, los 14 alérgenos
              obligatorios y cómo gestionarlos automáticamente en tu carta.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors"
            >
              Gestionar alérgenos con PLEKO
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="prose prose-lg max-w-none">

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Normativa de alérgenos en hostelería</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Desde 2014, el <strong>Reglamento (UE) 1169/2011</strong> obliga a todos los establecimientos de hostelería en la Unión Europea a informar sobre la presencia de alérgenos en sus platos. En España, esta normativa se aplica mediante el <strong>Real Decreto 126/2015</strong>.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              La obligación aplica a <strong>todos los establecimientos</strong>: restaurantes, bares, cafeterías, catering, comedores escolares, hospitales y cualquier negocio que sirva alimentos no envasados.
            </p>

            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-10">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-red-800 mb-2">Sanciones por incumplimiento</p>
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Leve</p>
                      <p className="text-red-600 font-bold text-lg">100€ - 5.000€</p>
                      <p className="text-slate-500 text-xs">Errores menores, información incompleta</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Grave</p>
                      <p className="text-red-600 font-bold text-lg">5.001€ - 20.000€</p>
                      <p className="text-slate-500 text-xs">Negligencia, información incorrecta</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="font-bold text-slate-800">Muy grave</p>
                      <p className="text-red-600 font-bold text-lg">20.001€ - 600.000€</p>
                      <p className="text-slate-500 text-xs">Riesgo para la salud, ocultación</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Los 14 alérgenos de declaración obligatoria</h2>
            <div className="grid md:grid-cols-2 gap-3 mb-10">
              {alergenos.map(({ nombre, desc, icono }) => (
                <div key={nombre} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-2xl flex-shrink-0">{icono}</span>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{nombre}</p>
                    <p className="text-slate-500 text-xs">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Cómo debes informar a tus clientes?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La normativa permite varias formas de comunicar los alérgenos:
            </p>
            <div className="space-y-3 mb-10">
              {[
                {
                  title: 'Iconos en la carta',
                  desc: 'Incluir los iconos de los alérgenos junto a cada plato en la carta impresa o digital. Es la opción más cómoda para el cliente.',
                  recomendado: true,
                },
                {
                  title: 'Carta de alérgenos separada',
                  desc: 'Un documento adicional que lista los alérgenos de cada plato. Debe estar accesible para los clientes sin que tengan que pedirla.',
                  recomendado: false,
                },
                {
                  title: 'Información verbal (con cartel)',
                  desc: 'Informar verbalmente si el cliente pregunta, pero debe haber un cartel visible indicando que la información está disponible. Opción mínima, no recomendada.',
                  recomendado: false,
                },
                {
                  title: 'Carta digital con alérgenos automáticos',
                  desc: 'PLEKO genera la información de alérgenos automáticamente a partir de los ingredientes de cada receta. Se muestra en la carta digital QR sin trabajo manual.',
                  recomendado: true,
                },
              ].map(({ title, desc, recomendado }) => (
                <div key={title} className={`p-4 rounded-xl border ${recomendado ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-slate-800">{title}</p>
                    {recomendado && <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded font-medium">Recomendado</span>}
                  </div>
                  <p className="text-slate-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Cómo PLEKO automatiza la gestión de alérgenos</h2>
            <div className="space-y-4 mb-10">
              {[
                {
                  num: '1',
                  title: 'Asigna alérgenos a cada ingrediente',
                  desc: 'Al crear un ingrediente (ej: "Harina de trigo"), marcas los alérgenos que contiene (gluten). Solo hay que hacerlo una vez.',
                },
                {
                  num: '2',
                  title: 'Las recetas heredan los alérgenos',
                  desc: 'Al crear una receta con harina, leche y huevos, PLEKO muestra automáticamente: gluten, lácteos y huevos. Sin introducir datos extra.',
                },
                {
                  num: '3',
                  title: 'La carta digital los muestra al cliente',
                  desc: 'En la carta digital con QR, cada plato muestra los iconos de sus alérgenos. El cliente puede filtrar platos por alérgenos que quiere evitar.',
                },
              ].map(({ num, title, desc }) => (
                <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 text-sm">{num}</span>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">{title}</p>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre alérgenos</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-8 text-center text-white">
            <ShieldCheck className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Cumple la normativa de alérgenos sin esfuerzo</h2>
            <p className="text-red-100 mb-6 max-w-xl mx-auto">
              PLEKO gestiona los alérgenos automáticamente a partir de los ingredientes de cada receta.
              Carta digital con alérgenos incluida. 14 días gratis.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-red-600 font-bold rounded-xl hover:bg-red-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
